import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Delete, Edit, ReportProblem } from "@mui/icons-material";
import {
  useGetSheqIncidentsQuery,
  useCreateSheqIncidentMutation,
  useUpdateSheqIncidentMutation,
  useDeleteSheqIncidentMutation,
  SheqIncident,
  IncidentType,
  IncidentSeverity,
  IncidentStatus,
} from "../../../redux/sheqApi";
import { useGetComsBlocksQuery } from "../../../redux/comsApi";

const NAVY = "#123456";

const INCIDENT_TYPES: { value: IncidentType; label: string }[] = [
  { value: "NEAR_MISS", label: "Near Miss" },
  { value: "FIRST_AID", label: "First Aid" },
  { value: "MEDICAL_TREATMENT", label: "Medical Treatment" },
  { value: "LOST_TIME", label: "Lost Time Injury" },
  { value: "FATALITY", label: "Fatality" },
  { value: "PROPERTY_DAMAGE", label: "Property Damage" },
  { value: "ENVIRONMENTAL", label: "Environmental" },
  { value: "UNSAFE_ACT", label: "Unsafe Act" },
  { value: "UNSAFE_CONDITION", label: "Unsafe Condition" },
];

const SEVERITIES: IncidentSeverity[] = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];
const STATUSES: IncidentStatus[] = ["OPEN", "UNDER_INVESTIGATION", "CLOSED"];

const SEVERITY_COLOR: Record<IncidentSeverity, string> = {
  LOW: "#2e7d32",
  MEDIUM: "#f57c00",
  HIGH: "#c62828",
  CRITICAL: "#4a148c",
};

const STATUS_COLOR: Record<IncidentStatus, string> = {
  OPEN: "#c62828",
  UNDER_INVESTIGATION: "#f57c00",
  CLOSED: "#2e7d32",
};

function today() {
  return new Date().toISOString().slice(0, 10);
}

// ─── Incident dialog ────────────────────────────────────────────────────────
interface IncidentDialogProps {
  open: boolean;
  incident: SheqIncident | null;
  onClose: () => void;
}

const emptyIncident = (): Partial<SheqIncident> => ({
  title: "",
  incidentDate: today(),
  type: "NEAR_MISS",
  severity: "MEDIUM",
  description: "",
  status: "OPEN",
  medicalAttentionRequired: false,
  daysLost: 0,
  reportedToDoL: false,
});

const IncidentDialog: React.FC<IncidentDialogProps> = ({
  open,
  incident,
  onClose,
}) => {
  const { data: blocks = [] } = useGetComsBlocksQuery();
  const [create, { isLoading: creating }] = useCreateSheqIncidentMutation();
  const [update, { isLoading: updating }] = useUpdateSheqIncidentMutation();

  const [form, setForm] = useState<Partial<SheqIncident>>(
    incident ?? emptyIncident(),
  );

  React.useEffect(() => {
    setForm(incident ?? emptyIncident());
  }, [incident, open]);

  const set = (key: keyof SheqIncident, val: unknown) =>
    setForm((f) => ({ ...f, [key]: val }));

  const editing = !!incident;
  const saving = creating || updating;

  const handleSave = async () => {
    if (!form.title || !form.incidentDate || !form.type || !form.description)
      return;
    const block = blocks.find((b) => b.id === form.blockId);
    const payload = {
      ...form,
      blockName: block?.name ?? form.blockName,
      medicalAttentionRequired: form.medicalAttentionRequired ?? false,
      daysLost: form.daysLost ?? 0,
      reportedToDoL: form.reportedToDoL ?? false,
    } as Omit<SheqIncident, "id" | "createdAt" | "updatedAt">;
    try {
      if (editing && incident?.id) {
        await update({ id: incident.id, body: payload }).unwrap();
      } else {
        await create(payload).unwrap();
      }
      onClose();
    } catch {
      // handle
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{editing ? "Edit Incident" : "Log Incident"}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField
            label="Incident Title"
            size="small"
            required
            value={form.title ?? ""}
            onChange={(e) => set("title", e.target.value)}
          />

          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              label="Date"
              type="date"
              size="small"
              required
              value={form.incidentDate ?? ""}
              onChange={(e) => set("incidentDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: "1 1 140px" }}
            />
            <TextField
              label="Time"
              type="time"
              size="small"
              value={form.incidentTime ?? ""}
              onChange={(e) => set("incidentTime", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: "1 1 120px" }}
            />
            <FormControl size="small" sx={{ flex: "1 1 180px" }}>
              <InputLabel>Site / Block</InputLabel>
              <Select
                label="Site / Block"
                value={form.blockId ?? ""}
                onChange={(e) => set("blockId", e.target.value)}
              >
                <MenuItem value="">— None —</MenuItem>
                {blocks.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Location / Area"
              size="small"
              value={form.location ?? ""}
              onChange={(e) => set("location", e.target.value)}
              sx={{ flex: "1 1 180px" }}
            />
          </Box>

          <Box display="flex" gap={2} flexWrap="wrap">
            <FormControl size="small" sx={{ flex: "1 1 180px" }} required>
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={form.type ?? "NEAR_MISS"}
                onChange={(e) => set("type", e.target.value)}
              >
                {INCIDENT_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: "1 1 140px" }}>
              <InputLabel>Severity</InputLabel>
              <Select
                label="Severity"
                value={form.severity ?? "MEDIUM"}
                onChange={(e) => set("severity", e.target.value)}
              >
                {SEVERITIES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: "1 1 180px" }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={form.status ?? "OPEN"}
                onChange={(e) => set("status", e.target.value)}
              >
                {STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <TextField
            label="Description"
            size="small"
            multiline
            rows={3}
            required
            value={form.description ?? ""}
            onChange={(e) => set("description", e.target.value)}
          />

          {/* Injured person */}
          <Typography
            variant="caption"
            fontWeight={600}
            color="text.secondary"
            mt={1}
          >
            INJURED / AFFECTED PERSON
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              label="Name"
              size="small"
              value={form.injuredPersonName ?? ""}
              onChange={(e) => set("injuredPersonName", e.target.value)}
              sx={{ flex: "1 1 180px" }}
            />
            <TextField
              label="Role / Trade"
              size="small"
              value={form.injuredPersonRole ?? ""}
              onChange={(e) => set("injuredPersonRole", e.target.value)}
              sx={{ flex: "1 1 160px" }}
            />
            <TextField
              label="Body part affected"
              size="small"
              value={form.bodyPartAffected ?? ""}
              onChange={(e) => set("bodyPartAffected", e.target.value)}
              sx={{ flex: "1 1 160px" }}
            />
          </Box>
          <Box display="flex" gap={3} flexWrap="wrap" alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={form.medicalAttentionRequired ?? false}
                  onChange={(e) =>
                    set("medicalAttentionRequired", e.target.checked)
                  }
                  size="small"
                />
              }
              label="Medical attention required"
            />
            <TextField
              label="Days lost"
              type="number"
              size="small"
              value={form.daysLost ?? 0}
              onChange={(e) => set("daysLost", Number(e.target.value))}
              inputProps={{ min: 0 }}
              sx={{ width: 110 }}
            />
          </Box>

          {/* Investigation */}
          <Typography
            variant="caption"
            fontWeight={600}
            color="text.secondary"
            mt={1}
          >
            INVESTIGATION
          </Typography>
          <TextField
            label="Root Cause"
            size="small"
            multiline
            rows={2}
            value={form.rootCause ?? ""}
            onChange={(e) => set("rootCause", e.target.value)}
          />
          <TextField
            label="Corrective Actions"
            size="small"
            multiline
            rows={2}
            value={form.correctiveActions ?? ""}
            onChange={(e) => set("correctiveActions", e.target.value)}
          />
          <TextField
            label="Preventive Actions"
            size="small"
            multiline
            rows={2}
            value={form.preventiveActions ?? ""}
            onChange={(e) => set("preventiveActions", e.target.value)}
          />

          {/* Reporting */}
          <Typography
            variant="caption"
            fontWeight={600}
            color="text.secondary"
            mt={1}
          >
            REPORTING
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <TextField
              label="Reported by"
              size="small"
              value={form.reportedByName ?? ""}
              onChange={(e) => set("reportedByName", e.target.value)}
              sx={{ flex: "1 1 180px" }}
            />
            <TextField
              label="Witness names"
              size="small"
              value={form.witnessNames ?? ""}
              onChange={(e) => set("witnessNames", e.target.value)}
              sx={{ flex: "1 1 200px" }}
            />
          </Box>
          <Box display="flex" gap={3} flexWrap="wrap" alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={form.reportedToDoL ?? false}
                  onChange={(e) => set("reportedToDoL", e.target.checked)}
                  size="small"
                />
              }
              label="Reported to Department of Labour"
            />
            {form.reportedToDoL && (
              <TextField
                label="DoL Reference Number"
                size="small"
                value={form.dolReferenceNumber ?? ""}
                onChange={(e) => set("dolReferenceNumber", e.target.value)}
                sx={{ width: 200 }}
              />
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !form.title || !form.description}
          sx={{ bgcolor: NAVY }}
        >
          {saving ? (
            <CircularProgress size={16} />
          ) : editing ? (
            "Save"
          ) : (
            "Log Incident"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main component ─────────────────────────────────────────────────────────
interface Props {
  blockId?: string;
}

export const SheqIncidents: React.FC<Props> = ({ blockId }) => {
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editIncident, setEditIncident] = useState<SheqIncident | null>(null);

  const { data: incidents = [], isLoading } = useGetSheqIncidentsQuery({
    blockId: blockId || undefined,
    status: statusFilter || undefined,
    type: typeFilter || undefined,
  });
  const [deleteIncident] = useDeleteSheqIncidentMutation();

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this incident record?")) return;
    await deleteIncident(id);
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="h4" fontWeight={700}>
          Incidents
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setEditIncident(null);
            setDialogOpen(true);
          }}
          sx={{ bgcolor: NAVY }}
        >
          Log Incident
        </Button>
      </Box>

      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">All statuses</MenuItem>
            {STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Type</InputLabel>
          <Select
            label="Type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <MenuItem value="">All types</MenuItem>
            {INCIDENT_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : incidents.length === 0 ? (
        <Box textAlign="center" py={8}>
          <ReportProblem sx={{ fontSize: 56, color: "#ccc", mb: 2 }} />
          <Typography color="text.secondary">No incidents recorded.</Typography>
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap={2}>
          {incidents.map((inc) => (
            <Card key={inc.id} sx={{ p: 2 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                flexWrap="wrap"
                gap={1}
              >
                <Box flex={1} minWidth={0}>
                  <Box
                    display="flex"
                    gap={1}
                    alignItems="center"
                    mb={0.5}
                    flexWrap="wrap"
                  >
                    <Typography variant="body1" fontWeight={700}>
                      {inc.title}
                    </Typography>
                    <Chip
                      label={
                        INCIDENT_TYPES.find((t) => t.value === inc.type)
                          ?.label ?? inc.type
                      }
                      size="small"
                      sx={{ fontSize: 11 }}
                    />
                    <Chip
                      label={inc.severity}
                      size="small"
                      sx={{
                        bgcolor:
                          SEVERITY_COLOR[inc.severity as IncidentSeverity] ??
                          "#555",
                        color: "#fff",
                        fontSize: 11,
                      }}
                    />
                    <Chip
                      label={inc.status}
                      size="small"
                      sx={{
                        bgcolor:
                          STATUS_COLOR[inc.status as IncidentStatus] ?? "#555",
                        color: "#fff",
                        fontSize: 11,
                      }}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" mb={0.5}>
                    {inc.incidentDate}
                    {inc.blockName ? ` · ${inc.blockName}` : ""}
                    {inc.location ? ` · ${inc.location}` : ""}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {inc.description}
                  </Typography>
                  {inc.injuredPersonName && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      mt={0.5}
                      display="block"
                    >
                      Injured: {inc.injuredPersonName}
                      {inc.daysLost ? ` · ${inc.daysLost} days lost` : ""}
                    </Typography>
                  )}
                </Box>
                <Box display="flex" gap={0.5}>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditIncident(inc);
                        setDialogOpen(true);
                      }}
                    >
                      <Edit sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(inc.id!)}
                    >
                      <Delete sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Card>
          ))}
        </Box>
      )}

      <IncidentDialog
        open={dialogOpen}
        incident={editIncident}
        onClose={() => {
          setDialogOpen(false);
          setEditIncident(null);
        }}
      />
    </Box>
  );
};
